import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {Panel} from 'primeng/panel';
import {TableModule} from 'primeng/table';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {Divider} from 'primeng/divider';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService} from 'primeng/api';
import {MessageService} from 'primeng/api';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import { CommonEffects } from '../store/common.effects';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectLccaPeriodicalData, selectLccaPeriodicalTotal } from '../store/common.selector';
import { CatalogLccaPeriodicalsResponse } from '../../shared/models/common/lcca-periodical.model';
import { selectIsLoadingAction } from '../../shared/store/global';
import { takeUntil } from 'rxjs/operators';
import * as action from '../store/common.actions';
import {toSignal} from '@angular/core/rxjs-interop';
import {BaseListComponent} from '../../shared/components/base-list.component';
import { AssetHistoryDialogComponent } from '../../shared/components/asset-history-dialog/asset-history-dialog.component';
import { ColumnFilters } from '../../shared/models/ui/table-column.model';

@Component({
  selector: 'lcca-periodicals-list',
  templateUrl: './lcca-periodicals-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Divider,
    ConfirmDialogModule,
    DialogModule,
    ToastModule,
    AssetHistoryDialogComponent
  ],
  providers: [ConfirmationService, MessageService, CommonEffects],
  styleUrls: ['./lcca-periodicals-list.component.scss']
})
export class LccaPeriodicalsListComponent extends BaseListComponent implements OnInit, OnDestroy {

  private messageService = inject(MessageService);
  private store$ = inject(Store<{ common: any }>)
  private actions$ = inject(Actions);

  lccaPeriodicals = toSignal(this.store$.select(selectLccaPeriodicalData), {initialValue: []});
  totalRecords = toSignal(this.store$.select(selectLccaPeriodicalTotal), {initialValue: 0});
  loading = toSignal(this.store$.select(selectIsLoadingAction('[Common] Find LCCA Periodicals')), {initialValue: false});

  
  readonly auditResourceName = 'Lcca Periodicals';
  historyVisible = false;
  historyRecordId: number | string | null = null;
  historyRecordLabel = '';

  selectedLccaPeriodicals = signal<CatalogLccaPeriodicalsResponse[]>([]);

  // Add LCCA Periodical Form Variables
  dialogVisible = signal(false);
  lccaPeriodicalName = signal('');
  savingLccaPeriodical = signal(false);
  isEditMode = signal(false);
  editingLccaPeriodicalId = signal<number | null>(null);

  deleteButtonLabel = computed(() => {
    return this.selectedLccaPeriodicals().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  override ngOnInit(): void {
    super.ngOnInit();

    // Success Alert
    this.actions$.pipe(
      ofType(action.createLccaPeriodicalSuccess, action.updateLccaPeriodicalSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingLccaPeriodical.set(false);
      this.closeDialog();
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    })

    // Delete Success Alert
    this.actions$.pipe(
      ofType(action.deleteLccaPeriodicalSuccess, action.bulkDeleteLccaPeriodicalsSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.selectedLccaPeriodicals.set([]);
      this.loadLccaPeriodicals(); 
    })

    // Handle Error
    this.actions$.pipe(
      ofType(action.createLccaPeriodicalError, action.updateLccaPeriodicalError, action.deleteLccaPeriodicalError, action.bulkDeleteLccaPeriodicalsError),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingLccaPeriodical.set(false);
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    const keywords = filters['keywords'] || '';
    this.store$.dispatch(action.findLccaPeriodicals({
      page,
      size,
      keywords
    }));
  }

  loadLccaPeriodicals(): void {
    this.store$.dispatch(action.findLccaPeriodicals({ page: 1, size: this.rows, keywords: ''}))
  }

  addNew(): void {
    this.lccaPeriodicalName.set('');
    this.isEditMode.set(false);
    this.dialogVisible.set(true);
    this.editingLccaPeriodicalId.set(null);
  }

  closeDialog(): void {
    this.lccaPeriodicalName.set('');
    this.savingLccaPeriodical.set(false);
    this.isEditMode.set(false);
    this.dialogVisible.set(false);
    this.editingLccaPeriodicalId.set(null);
  }

  saveLccaPeriodical(): void {
    if(!this.lccaPeriodicalName().trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'LCCA periodical name is required'
      });
      return;
    }

    this.savingLccaPeriodical.set(true);

    const lccaPeriodicalData = {
      name: this.lccaPeriodicalName().trim()
    };

    if(this.isEditMode() && this.editingLccaPeriodicalId()) {
      this.store$.dispatch(action.updateLccaPeriodical({ id: this.editingLccaPeriodicalId()!, lccaPeriodical: lccaPeriodicalData }))
    } else {
      this.store$.dispatch(action.createLccaPeriodical({ lccaPeriodical: lccaPeriodicalData }))
    }
  }

  editLccaPeriodical(): void {
    if (this.selectedLccaPeriodicals().length === 1) {
      const lccaPeriodicalToEdit = this.selectedLccaPeriodicals()[0];
      this.lccaPeriodicalName.set(lccaPeriodicalToEdit.name ? lccaPeriodicalToEdit.name : '');
      this.isEditMode.set(true);
      this.editingLccaPeriodicalId.set(lccaPeriodicalToEdit.id);
      this.dialogVisible.set(true);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one LCCA periodical to edit'
      });
    }
  }

  deleteLccaPeriodical(): void {
    if (this.selectedLccaPeriodicals().length === 0) {
      return;
    }

    const lccaPeriodicalsToDelete = [...this.selectedLccaPeriodicals()];
    const count = lccaPeriodicalsToDelete.length;
    
    let message: string;
    if (count === 1) {
      message = `Are you sure you want to delete "${lccaPeriodicalsToDelete[0].name}"?`;
    } else {
      const maxDisplay = 10;
      const namesToShow = lccaPeriodicalsToDelete.slice(0, maxDisplay);
      const namesList = namesToShow.map((p, index) => `${index + 1}. ${p.name}`).join('<br>');
      
      let fullMessage = `Are you sure you want to delete ${count} LCCA periodicals?<br><br><strong>Names to be deleted:</strong><br>${namesList}`;
      
      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }
      
      message = fullMessage;
    }

    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (count === 1) {
          this.store$.dispatch(action.deleteLccaPeriodical({ id: lccaPeriodicalsToDelete[0].id }));
        } else {
          const ids = lccaPeriodicalsToDelete.map(p => p.id);
          this.store$.dispatch(action.bulkDeleteLccaPeriodicals({ ids }));
        }
      }
    });
  }

  refreshLccaPeriodicals(): void {
    this.selectedLccaPeriodicals.set([]);
    this.clearFilter();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'LCCA periodicals list has been refreshed'
    });
  }

  showHistory(): void {
    const selected = this.selectedLccaPeriodicals();
    if (selected.length !== 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'History',
        detail: 'Select one record to view history'
      });
      return;
    }
    const item = selected[0] as any;
    this.historyRecordId = item?.id ?? null;
    this.historyRecordLabel = item?.name || item?.code || String(this.historyRecordId ?? '');
    this.historyVisible = true;
  }
}
